// 騎士と花嫁の固有の致命の FX (DEV ?execUnique=1・src/exec_unique.ts の段取りを読んで描くだけ・判定には関わらない)。
//
// 騎士「光の帯」(ユーザー 10-09「A 光の帯」・見本 C:\dev\LUNAIRE-notes\assets\2026-10-09-exec-motion-art\fx\knight_slash_a_light.png):
//   見本の3コマ = ①振り抜いた直後: 穂先から細い光が伸び出す ②届き切った瞬間: 先ほど幅広で明るい細長い三日月の面が主人公の先まで・
//   縁は紫ににじみ、中を銀白と紫の火の粉が流れる ③約0.4秒後: 帯は消え、通った床に紫の霞と火の粉が帯の形に残る。
//   コードでは 3 層 (紫のにじみ・藤色の身・冷たい白の芯) と、ほどける粒、床の残り火で描く。
//   決まり (台帳・10-09 の発注文): 円・輪・同心円・放射状の線・記号で描かない。粒は小さな柔らかい点。
// 花嫁の♡ (ユーザー 10-09「数とかはいいんだけど、近寄ったら灰色にする必要はない」・見本 fx/bride_hearts.png):
//   淡い銀白で半ば透け、内側に桜色のにじみ。輪郭線で描かない。赤は使わない (赤 = 弾けない)。
//   止まりの入りに残っている♡は、ぽんと弾けて淡い銀白と桜色の光の粒 6〜8 個になって上へ散り、200ms で消える (設計図 §0-2・§4-6)。
import type { GameState, SpriteKey } from './types'
import { arenaBoundsFor } from './balance'
import { bossSpriteFileStem, loadedBossSpriteArtPlacement, projectBossSpriteSourcePoint } from './sprites'
import {
  BRIDE_HEAD_TOP_SRC, BRIDE_OKAERI, brideHopLiftPx, brideImageAt, execHeartPx, execUniquePoseAt, heartStateAt,
  KNIGHT_BACKTURN, KNIGHT_CHARGE, KNIGHT_CHARGE_TIP_SRC, KNIGHT_EXEC_ART, KNIGHT_EXEC_TIP_SRC, knightBladePhase, knightChargeRunImageAt, knightEmbersLevel,
  knightWakeLengthPx,
  type BrideOkaeriPlan, type KnightBackturnPlan,
} from './exec_unique'

// 色 (紫 = パリィ可の重い一撃のオーラと同じ家族・冷たい白銀。金と赤は使わない)。
const HAZE = '120,78,186'      // にじみ (外側の紫)
const BODY = '190,160,240'     // 身 (藤色)
const CORE = '242,240,255'     // 芯 (冷たい白)
const EMBER_A = '176,128,236'  // 残り火 (紫)
const EMBER_B = '226,214,255'  // 残り火 (銀白)
const SAKURA = '248,204,222'   // ♡の内側の桜色 (赤に寄らない淡い色)
const SILVER = '242,238,250'   // ♡の淡い銀白
/** ♡と弾けた粒に使う色 (灰色にしない・赤に寄せない = tests/exec_unique.test.ts が見張る)。 */
export const HEART_COLORS = { sakura: SAKURA, silver: SILVER } as const

// 決まった乱数 (毎コマ同じ粒が同じ所に出る)。
function rnd(i: number, salt: number): number {
  const x = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453
  return x - Math.floor(x)
}

const ONE_TICK_MS = 1000 / 60

type KnightBladeGeo = { tipX: number; tipY: number; dir: 1 | -1; frontX: number; reachX: number; wallCut: boolean; parryCut: boolean }

/** 騎士の当たりからの経過 (ms)。active の頭 = 0。recovery は active の長さを足す。出ていなければ null。 */
function knightActiveMs(state: GameState): { ms: number; plan: KnightBackturnPlan } | null {
  const b = state.boss
  const atk = b.currentAttack
  const plan = atk?.uniqueExec
  if (!atk || !plan || plan.boss !== 'vigil-knight' || b.hp <= 0) return null
  // 当たり (ActiveHit) の時計は active の頭の tick で 1 コマ先に進む (当たった瞬間の止めの間も、光の帯は当たった所まで伸びて見える)。
  if (b.state === 'active') return { ms: b.stateElapsed * 1000 + ONE_TICK_MS, plan }
  if (b.state === 'recovery') return { ms: atk.activeMs + ONE_TICK_MS + b.stateElapsed * 1000, plan }
  return null
}

function knightBladeGeo(state: GameState, footY: number, height: number, frontPx: number): KnightBladeGeo | null {
  const b = state.boss
  const dir: 1 | -1 = b.facing === 'right' ? 1 : -1
  const placement = loadedBossSpriteArtPlacement('vigil-knight', KNIGHT_EXEC_ART.backturn as SpriteKey, b.x, footY, height, b.facing)
  const tip = placement ? projectBossSpriteSourcePoint(placement, [KNIGHT_EXEC_TIP_SRC.x, KNIGHT_EXEC_TIP_SRC.y]) : null
  if (!tip) return null
  const walls = arenaBoundsFor(b.id)
  const reachX = b.x + dir * frontPx
  let frontX = reachX
  let wallCut = false
  const wallX = dir > 0 ? walls.rightWall : walls.leftWall
  if ((frontX - wallX) * dir > 0) { frontX = wallX; wallCut = true }
  // 弾かれた: 光の帯は主人公の刃の所で砕ける (それより先へ描かない)。
  const p = state.player
  let parryCut = false
  if (p.state === 'parrying' && p.parryAbsorbed) {
    // 砕けた所 = 弾いた瞬間の主人公の刃 (押されて下がっても、帯の切れ目は弾いた所に残す)。
    const pushedPx = p.execPush ? p.execPush.dir * p.execPush.traveledPx : 0
    const cutX = p.x - pushedPx - dir * 14
    if ((frontX - cutX) * dir > 0) { frontX = cutX; parryCut = true }
  }
  if ((frontX - tip.x) * dir < 4) frontX = tip.x + dir * 4
  return { tipX: tip.x, tipY: tip.y, dir, frontX, reachX, wallCut, parryCut }
}

// 帯の形 (先ほど幅広・上の縁がふくらむ細長い三日月)。u = 0 (穂先) → 1 (前端)。
//   wake = 突進の光の尾 (10-12 K-3): 根元 (体の後ろ) も細くしない (前端の半分の幅のまま・端だけ丸く閉じる)。
//   細い根元は暗い背景の上で一本の筋に見える (10-10 ユーザー「線っぽいエフェクトはやめたい」) ので、体の後ろは幅のある淡い霞にする。
type BandProfile = 'band' | 'wake'
function bandHalfWidths(u: number, w: number, profile: BandProfile = 'band'): { up: number; down: number } {
  const grow = profile === 'wake' ? 0.5 + 0.5 * Math.pow(u, 0.8) : 0.18 + 0.82 * Math.pow(u, 0.75)
  const endTaper = u > 0.9 ? Math.sqrt(Math.max(0, 1 - (u - 0.9) / 0.1)) : 1
  const rootTaper = profile === 'wake' && u < 0.12 ? Math.sqrt(u / 0.12) : 1
  const k = w * grow * endTaper * rootTaper
  return { up: k * (0.62 + 0.25 * Math.sin(Math.PI * u)), down: k * 0.38 }
}

function traceBand(ctx: CanvasRenderingContext2D, g: KnightBladeGeo, w: number, lift: number, profile: BandProfile = 'band'): void {
  const N = 28
  const len = g.frontX - g.tipX
  ctx.beginPath()
  for (let i = 0; i <= N; i++) {
    const u = i / N
    const { up } = bandHalfWidths(u, w, profile)
    const x = g.tipX + len * u
    const y = g.tipY - up - lift * Math.sin(Math.PI * u)
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
  }
  for (let i = N; i >= 0; i--) {
    const u = i / N
    const { down } = bandHalfWidths(u, w, profile)
    ctx.lineTo(g.tipX + len * u, g.tipY + down - lift * 0.35 * Math.sin(Math.PI * u))
  }
  ctx.closePath()
}

function softDot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rgb: string, a: number): void {
  if (a <= 0.003) return
  const gr = ctx.createRadialGradient(x, y, 0, x, y, r * 2.2)
  gr.addColorStop(0, `rgba(${rgb},${a})`)
  gr.addColorStop(0.45, `rgba(${rgb},${a * 0.45})`)
  gr.addColorStop(1, `rgba(${rgb},0)`)
  ctx.fillStyle = gr
  ctx.fillRect(x - r * 2.2, y - r * 2.2, r * 4.4, r * 4.4)
}

function drawKnightBlade(ctx: CanvasRenderingContext2D, state: GameState, footY: number, height: number, ms: number): void {
  const ph = knightBladePhase(ms)
  if (!ph) return
  const g = knightBladeGeo(state, footY, height, ph.frontPx)
  if (!g) return
  if (import.meta.env.DEV) fxProbe().blade = { tipX: g.tipX, tipY: g.tipY, frontX: g.frontX }
  drawBandShape(ctx, g, ms, ph, 'all')
}

type BandPhase = { bodyAlpha: number; unravel: number }
/** 光の帯の形 (前の版の帯と、突進の光の尾で共有)。g.tipX = 帯の根元 (細い方)・g.frontX = 前端 (幅広で明るい方)。
 *  part: 'all' = 全部 / 'body' = 帯の身 (にじみ・藤色・芯) だけ / 'sparkle' = 前端のにじみ・火の粉・砕けた粉だけ。 */
function drawBandShape(ctx: CanvasRenderingContext2D, g: KnightBladeGeo, ms: number, ph: BandPhase, part: 'all' | 'body' | 'sparkle',
  opts: BandOpts = {}): void {
  const a = ph.bodyAlpha
  const thin = 1 - 0.55 * ph.unravel
  const W = 22 * thin               // 先の半幅の目安 (px)
  const len = g.frontX - g.tipX
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  if (part !== 'sparkle') drawBandBody(ctx, g, a, W, ph.unravel, opts)
  ctx.shadowBlur = 0
  if (part === 'body') { ctx.restore(); return }
  drawBandSparkle(ctx, g, ms, ph, a, W, len)
  ctx.restore()
}

type BandOpts = { coreFromTip?: boolean; coreMul?: number; wake?: boolean }
/** 帯の身 (1 紫のにじみ・2 藤色の身・3 冷たい白の芯)。突進の光の尾 (coreFromTip) は芯を前 (穂先側) だけに灯す
 *  (尾の後ろまで白い筋を引かない = 効果線に見せない・10-10 ユーザー「線っぽいエフェクトはやめたい」)。
 *  wake (10-12 K-3「体の後ろまで長く見せる」): 体の後ろ (根元側) は紫の霞と藤色の身を淡く・幅広に残す。芯は前の 4 割だけ。 */
function drawBandBody(ctx: CanvasRenderingContext2D, g: KnightBladeGeo, a: number, W: number, unravel: number, opts: BandOpts): void {
  const profile: BandProfile = opts.wake ? 'wake' : 'band'
  // 1) 紫のにじみ (外側・ぼかす)。
  ctx.shadowColor = `rgba(${HAZE},${0.9 * a})`
  ctx.shadowBlur = opts.wake ? 26 : 22
  let grad = ctx.createLinearGradient(g.tipX, 0, g.frontX, 0)
  grad.addColorStop(0, `rgba(${HAZE},0)`)
  if (opts.wake) {
    grad.addColorStop(0.12, `rgba(${HAZE},${0.16 * a})`)
    grad.addColorStop(0.45, `rgba(${HAZE},${0.3 * a})`)
  } else {
    grad.addColorStop(0.25, `rgba(${HAZE},${0.22 * a})`)
  }
  grad.addColorStop(1, `rgba(${HAZE},${0.42 * a})`)
  ctx.fillStyle = grad
  traceBand(ctx, g, W * 2.0, 5, profile)
  ctx.fill()
  // 2) 藤色の身。
  ctx.shadowBlur = 10
  ctx.shadowColor = `rgba(${BODY},${0.8 * a})`
  grad = ctx.createLinearGradient(g.tipX, 0, g.frontX, 0)
  if (opts.wake) {
    grad.addColorStop(0, `rgba(${BODY},0)`)
    grad.addColorStop(0.18, `rgba(${BODY},${0.12 * a})`)
    grad.addColorStop(0.5, `rgba(${BODY},${0.34 * a})`)
  } else {
    grad.addColorStop(0, `rgba(${BODY},${0.15 * a})`)
    grad.addColorStop(0.5, `rgba(${BODY},${0.5 * a})`)
  }
  grad.addColorStop(1, `rgba(${BODY},${0.75 * a})`)
  ctx.fillStyle = grad
  traceBand(ctx, g, W * 1.05, 4, profile)
  ctx.fill()
  // 3) 冷たい白の芯 (ほどけ始めると先に消える)。
  const coreA = a * (opts.coreMul ?? 1) * (1 - Math.min(1, unravel * 1.6))
  if (coreA > 0.01) {
    ctx.shadowBlur = 6
    ctx.shadowColor = `rgba(${CORE},${coreA})`
    grad = ctx.createLinearGradient(g.tipX, 0, g.frontX, 0)
    const from = opts.wake ? 0.6 : 0.5
    if (opts.coreFromTip) {
      grad.addColorStop(0, `rgba(${CORE},0)`)
      grad.addColorStop(from, `rgba(${CORE},0)`)
    } else {
      grad.addColorStop(0, `rgba(${CORE},${0.35 * coreA})`)
    }
    grad.addColorStop(Math.max(0.6, from + 0.1), `rgba(${CORE},${(opts.coreFromTip ? 0.5 : 0.85) * coreA})`)
    grad.addColorStop(1, `rgba(${CORE},${coreA})`)
    ctx.fillStyle = grad
    traceBand(ctx, g, W * 0.42, 3, profile)
    ctx.fill()
  }
}

/** 帯の前端のにじみ・中を流れる火の粉・壁や刃で砕けた粉 (キャラの手前に描く物)。 */
function drawBandSparkle(ctx: CanvasRenderingContext2D, g: KnightBladeGeo, ms: number, ph: BandPhase, a: number, W: number, len: number): void {
  // 4) 前端の明るいにじみ (横に長い柔らかい光・輪にしない)。
  if (!g.parryCut) {
    const fx = g.frontX - g.dir * 10
    ctx.save()
    ctx.translate(fx, g.tipY - W * 0.25)
    ctx.scale(3.2, 1)
    const fr = ctx.createRadialGradient(0, 0, 0, 0, 0, W * 1.4)
    fr.addColorStop(0, `rgba(${CORE},${0.55 * a})`)
    fr.addColorStop(0.5, `rgba(${BODY},${0.22 * a})`)
    fr.addColorStop(1, `rgba(${BODY},0)`)
    ctx.fillStyle = fr
    ctx.fillRect(-W * 1.4, -W * 1.4, W * 2.8, W * 2.8)
    ctx.restore()
  }
  // 5) 中を流れる火の粉と、縁からほどける粒 (帯が消える間に外へ散る)。
  const n = 46
  for (let i = 0; i < n; i++) {
    const u = rnd(i, 1)
    if (u * Math.abs(len) > Math.abs(len)) continue
    const { up, down } = bandHalfWidths(u, W * 1.6)
    const side = rnd(i, 2) < 0.6 ? -1 : 1
    const off = side < 0 ? -up * rnd(i, 3) : down * rnd(i, 3)
    const flow = ((ms / 1000) * (0.8 + rnd(i, 4)) * 2.2 + rnd(i, 5)) % 1
    const x = g.tipX + len * Math.min(1, u + flow * 0.06) - g.dir * ph.unravel * 18 * rnd(i, 6)
    const y = g.tipY + off - ph.unravel * (10 + 22 * rnd(i, 7)) * (side < 0 ? 1 : 0.4)
    const tw = 0.6 + 0.4 * Math.sin(ms * 0.05 + i * 2.1)
    const r = 0.9 + 1.3 * rnd(i, 8)
    const rgb = rnd(i, 9) < 0.45 ? CORE : rnd(i, 9) < 0.8 ? BODY : HAZE
    softDot(ctx, x, y, r, rgb, Math.min(1, (0.35 + 0.65 * ph.unravel) * tw * (a * 0.7 + 0.3 * (1 - ph.unravel))))
  }
  // 6) 壁に当たった / 主人公の刃で砕けた: 紫と白銀の粉が散る (壁は上下へ・弾きは騎士の方へ)。
  if (g.wallCut || g.parryCut) {
    const t = Math.min(1, ms / KNIGHT_BACKTURN.bladeEndMs)
    for (let i = 0; i < 18; i++) {
      const ang = (rnd(i, 11) - 0.5) * (g.wallCut ? 2.6 : 1.8)
      const sp = 20 + 50 * rnd(i, 12)
      const vx = g.wallCut ? -g.dir * Math.abs(Math.sin(ang)) * sp * 0.35 : -g.dir * Math.cos(ang) * sp
      const vy = g.wallCut ? Math.cos(ang) * sp * (rnd(i, 13) < 0.5 ? -1 : 1) : Math.sin(ang) * sp
      softDot(ctx, g.frontX + vx * t, g.tipY + vy * t, 0.9 + 1.2 * rnd(i, 14), rnd(i, 15) < 0.5 ? CORE : EMBER_A, (1 - t) * 0.9)
    }
  }
}

// ---------------- 騎士の突進 (既定・10-12) の光の尾 ----------------
// 前の版の「光の帯」の見た目 (3 層の帯・流れる火の粉・床の残り火) をそのまま使い、帯の前端 = いま描いている絵の穂先・根元 = その後ろ。
//   騎士から離れて飛ぶ物にしない (ユーザー 10-12「衝撃波を弾いたのにのけぞってて、よくわからない」)。
//   走っている間: 帯の身はキャラの後ろ (騎士の体が光の前に立つ = 光をまとって突っ込む)・前端のにじみと火の粉は手前。
//   当たってから: 当たった所 (actionAnchorX) の K3 の穂先で止まり、前の版と同じ時間でほどけて床に残り火。

/** 突進の光の尾の形 (いま)。走る前・出ていない時は null。 */
type ChargeWake = { g: KnightBladeGeo; ph: BandPhase; ms: number; running: boolean; floorLen: number; plan: KnightBackturnPlan; nowMs: number }
function knightChargeWake(state: GameState, footY: number, height: number): ChargeWake | null {
  const b = state.boss
  const atk = b.currentAttack
  const plan = atk?.uniqueExec
  if (!atk || !plan || plan.boss !== 'vigil-knight' || plan.form !== 'charge' || b.hp <= 0) return null
  const C = KNIGHT_CHARGE
  const dir: 1 | -1 = b.facing === 'right' ? 1 : -1
  const runPx = plan.runFromGapPx - plan.strikeGapPx
  let image: string
  let x: number
  let ph: BandPhase
  let ms: number
  let traveled: number
  let running = false
  if (b.state === 'telegraph') {
    ms = b.stateElapsed * 1000
    const run = knightChargeRunImageAt(plan, ms)
    if (!run) return null
    image = run
    x = b.x
    traveled = Math.max(0, plan.runFromGapPx - execUniquePoseAt(plan, ms).gapPx)
    ph = { bodyAlpha: Math.min(1, (ms - plan.runStartMs + ONE_TICK_MS) / C.wakeFadeInMs), unravel: 0 }
    running = true
  } else {
    const k = knightActiveMs(state)
    if (!k) return null
    // 光の尾がほどけ切った後も、床の残り火のために形だけ返す (明るさ 0)。
    const bp = knightBladePhase(k.ms) ?? { bodyAlpha: 0, unravel: 1 }
    ms = k.ms
    image = KNIGHT_EXEC_ART.backturn
    x = b.actionAnchorX
    traveled = runPx
    ph = bp
  }
  const placement = loadedBossSpriteArtPlacement('vigil-knight', image as SpriteKey, x, footY, height, b.facing)
  const src = KNIGHT_CHARGE_TIP_SRC[image]
  const tip = placement && src ? projectBossSpriteSourcePoint(placement, [src.x, src.y]) : null
  if (!tip) return null
  const len = knightWakeLengthPx(traveled)
  let frontX = tip.x
  let parryCut = false
  const p = state.player
  if (!running && p.state === 'parrying' && p.parryAbsorbed) {
    // 弾かれた: 光は主人公の刃の所で砕ける (それより先へ描かない)。
    const pushedPx = p.execPush ? p.execPush.dir * p.execPush.traveledPx : 0
    const cutX = p.x - pushedPx - dir * 14
    if ((frontX - cutX) * dir > 0) { frontX = cutX; parryCut = true }
  }
  // 床の残り火は走った所だけ (光の尾は K-3 で体の後ろまで伸ばしたが、床に残すのは前の長さ = 110px + 走った距離・最大 320px)。
  const floorLen = Math.min(320, 110 + traveled)
  // nowMs = 溜めの頭からの時計 (当たってからは溜めの長さ + 当たりからの経過)。光の粒の生まれた時刻と同じ時計。
  const nowMs = running ? ms : plan.chargeMs + ms
  return { g: { tipX: tip.x - dir * len, tipY: tip.y, dir, frontX, reachX: tip.x, wallCut: false, parryCut }, ph, ms, running, floorLen, plan, nowMs }
}

/** 光の尾から落ちる光の粒 (10-12 K-3): 走っている間 12ms ごとに尾の中から 3 粒ずつ生まれ、生まれた所 (床の上の位置) に残って
 *  少し浮きながら 0.2〜0.36 秒で消える。騎士が 1100px/秒で抜けていくので、粒は尾の後ろへさらに 200〜400px の筋の無い光の道になる
 *  (memory「FX に線を描かない」= 火の粉のように散らす)。 */
function drawKnightWakeSparks(ctx: CanvasRenderingContext2D, state: GameState, footY: number, height: number, w: ChargeWake): void {
  const plan = w.plan
  const b = state.boss
  const dir = w.g.dir
  const C = KNIGHT_CHARGE
  // 走りの絵の穂先の、体の中心からの前への距離 (表示の大きさで)。
  const pl = loadedBossSpriteArtPlacement('vigil-knight', 'charge_run1' as SpriteKey, 0, footY, height, b.facing)
  const src = KNIGHT_CHARGE_TIP_SRC.charge_run1!
  const tip0 = pl ? projectBossSpriteSourcePoint(pl, [src.x, src.y]) : null
  if (!tip0) return
  // 主人公の位置 (崩れて動かない) = 体の位置 + 間合い。弾かれた後は当たった所から数える。
  const base = b.state === 'telegraph' ? b.x + dir * execUniquePoseAt(plan, w.nowMs).gapPx : b.actionAnchorX + dir * plan.strikeGapPx
  const end = Math.min(w.nowMs, plan.chargeMs)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  let n = 0
  for (let te = plan.runStartMs, k = 0; te <= end; te += 12, k++) {
    const age = w.nowMs - te
    const gapTe = execUniquePoseAt(plan, te).gapPx
    const tipX = base - dir * gapTe + tip0.x
    const traveled = Math.max(0, plan.runFromGapPx - gapTe)
    const len = knightWakeLengthPx(traveled)
    for (let j = 0; j < 3; j++) {
      const i = k * 3 + j
      const life = C.wakeSparkLifeMs * (0.65 + 0.55 * rnd(i, 61))
      if (age < 0 || age >= life) continue
      const t = age / life
      const along = Math.pow(rnd(i, 62), 0.7) * 0.85      // 穂先 0 → 尾の根元 1 (根元寄りを多めに)
      const x = tipX - dir * len * along - dir * (12 + 26 * rnd(i, 63)) * (age / 1000)
      const y = w.g.tipY + (rnd(i, 64) - 0.55) * 26 - (10 + 26 * rnd(i, 65)) * t
      const a = 0.85 * Math.pow(1 - t, 1.3) * (0.6 + 0.4 * Math.sin(age * 0.05 + i))
      softDot(ctx, x, y, 0.8 + 1.2 * rnd(i, 66), rnd(i, 67) < 0.4 ? CORE : rnd(i, 67) < 0.75 ? BODY : EMBER_A, a)
      n++
    }
  }
  ctx.restore()
  if (import.meta.env.DEV) fxProbe().wakeSparks = n
}

function drawKnightEmbers(ctx: CanvasRenderingContext2D, state: GameState, footY: number, height: number, ms: number): void {
  const level = knightEmbersLevel(ms)
  if (level <= 0.003) return
  // 床の帯は届き切った長さ (弾かれた時は主人公の刃まで) で固定する。突進は光の尾が通った所 (根元 → 当たった穂先)。
  const plan = state.boss.currentAttack?.uniqueExec
  const wk = plan?.boss === 'vigil-knight' && plan.form === 'charge' ? knightChargeWake(state, footY, height) : null
  const g = wk ? { ...wk.g, tipX: wk.g.reachX - wk.g.dir * wk.floorLen }
    : plan?.boss === 'vigil-knight' && plan.form === 'charge' ? null
    : knightBladeGeo(state, footY, height, KNIGHT_BACKTURN.bladeReachPx)
  if (!g) return
  const floorY = state.boss.y
  const x0 = g.tipX - g.dir * 30
  const x1 = g.frontX
  const len = x1 - x0
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // 紫の霞 (床を這う低い帯・端は薄れる)。
  const grad = ctx.createLinearGradient(x0, 0, x1, 0)
  grad.addColorStop(0, `rgba(${HAZE},0)`)
  grad.addColorStop(0.15, `rgba(${HAZE},${0.34 * level})`)
  grad.addColorStop(0.85, `rgba(${HAZE},${0.4 * level})`)
  grad.addColorStop(1, `rgba(${HAZE},0)`)
  ctx.fillStyle = grad
  ctx.shadowColor = `rgba(${HAZE},${0.6 * level})`
  ctx.shadowBlur = 14
  ctx.beginPath()
  const N = 24
  for (let i = 0; i <= N; i++) {
    const u = i / N
    const hgt = (6 + 5 * Math.sin(u * 9.3 + ms * 0.004) + 3 * rnd(i, 21)) * (0.6 + 0.4 * level)
    const x = x0 + len * u
    if (i === 0) ctx.moveTo(x, floorY + 2); else ctx.lineTo(x, floorY - hgt)
  }
  ctx.lineTo(x1, floorY + 3)
  ctx.lineTo(x0, floorY + 3)
  ctx.closePath()
  ctx.fill()
  ctx.shadowBlur = 0
  // 火の粉 (またたきながら少し浮いて消える)。
  for (let i = 0; i < 64; i++) {
    const u = rnd(i, 31)
    const life = 0.35 + 0.5 * rnd(i, 32)
    const phase = ((ms / 1000) / life + rnd(i, 33)) % 1
    const x = x0 + len * u + Math.sin(phase * 5 + i) * 2
    const y = floorY - 2 - phase * (8 + 18 * rnd(i, 34))
    const tw = Math.sin(Math.PI * phase) * (0.55 + 0.45 * Math.sin(ms * 0.03 + i * 1.7))
    softDot(ctx, x, y, 0.8 + 1.1 * rnd(i, 35), rnd(i, 36) < 0.55 ? EMBER_A : EMBER_B, Math.max(0, tw) * level)
  }
  ctx.restore()
}

// ---------------- 花嫁の♡ ----------------
function heartPath(ctx: CanvasRenderingContext2D, size: number): void {
  // 中心 (0,0)・幅 size のふっくらした丸いハート。
  const s = size / 2
  ctx.beginPath()
  ctx.moveTo(0, s * 0.95)
  ctx.bezierCurveTo(-s * 0.55, s * 0.55, -s * 1.08, s * 0.18, -s * 1.0, -s * 0.28)
  ctx.bezierCurveTo(-s * 0.94, -s * 0.82, -s * 0.22, -s * 0.98, 0, -s * 0.42)
  ctx.bezierCurveTo(s * 0.22, -s * 0.98, s * 0.94, -s * 0.82, s * 1.0, -s * 0.28)
  ctx.bezierCurveTo(s * 1.08, s * 0.18, s * 0.55, s * 0.55, 0, s * 0.95)
  ctx.closePath()
}

/** 花嫁の♡を1つ (輪郭線なし・桜色と銀白)。抱擁の拘束の♡ (src/bride_hug_bind.ts) もこれで描く。 */
export function drawHeart(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, alpha: number, k: number, loosen = 0): void {
  if (alpha <= 0.003) return
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate((rnd(k, 41) - 0.5) * 0.35)
  ctx.scale(0.95 + 0.08 * rnd(k, 42), 1)
  // にじむ縁 (輪郭線は描かない)。弾ける時はにじみを広げて形をほどく。
  ctx.shadowColor = `rgba(${SAKURA},${0.65 * alpha})`
  ctx.shadowBlur = 6 + 10 * loosen
  const gr = ctx.createRadialGradient(-size * 0.08, -size * 0.05, 0, 0, 0, size * 0.62)
  gr.addColorStop(0, `rgba(${SAKURA},${0.78 * alpha})`)
  gr.addColorStop(0.55, `rgba(${SAKURA},${0.48 * alpha})`)
  gr.addColorStop(1, `rgba(${SILVER},${0.5 * alpha})`)
  ctx.fillStyle = gr
  heartPath(ctx, size)
  ctx.fill()
  ctx.shadowBlur = 0
  // 小さなつや (左上)。
  softDot(ctx, -size * 0.24, -size * 0.2, size * 0.07, '255,255,255', 0.75 * alpha * (1 - loosen))
  ctx.restore()
}

function drawBrideHearts(ctx: CanvasRenderingContext2D, state: GameState, plan: BrideOkaeriPlan, footY: number, height: number): void {
  const b = state.boss
  if (b.state !== 'telegraph' || b.hp <= 0) return
  const ms = b.stateElapsed * 1000
  const size = execHeartPx()
  const towardPlayer = state.player.x >= b.x ? 1 : -1
  ctx.save()
  for (let k = 0; k < plan.heartBirthMs.length; k++) {
    const hs = heartStateAt(plan, k, ms)
    if (!hs) continue
    // 生まれた瞬間の頭のてっぺん (その時の絵・位置・弾みの高さ)。
    const born = plan.heartBirthMs[k]!
    const bx = state.player.x - towardPlayer * execUniquePoseAt(plan, born).gapPx
    const image = brideImageAt(plan, born)
    const placement = loadedBossSpriteArtPlacement('bride', image as SpriteKey, bx, footY, height, b.facing)
    const src = BRIDE_HEAD_TOP_SRC[bossSpriteFileStem('bride', image)] ?? BRIDE_HEAD_TOP_SRC[image]
    const head = placement && src ? projectBossSpriteSourcePoint(placement, src) : null
    const hx = head?.x ?? bx
    const hy = (head?.y ?? footY - height) - brideHopLiftPx(plan, born) - size * 0.6 - 4
    // 少し後ろ (主人公と逆) へ漂い、左右にゆらぐ (driftPx は負 = 後ろへ)。
    const x = hx + towardPlayer * hs.driftPx + hs.swayPx
    const y = hy - hs.risePx
    if (import.meta.env.DEV) fxProbe().hearts.push({ k, kind: hs.kind, x, y, size: size * (hs.kind === 'float' ? hs.scale : 1) })
    if (hs.kind === 'float') {
      drawHeart(ctx, x, y, size * hs.scale, hs.alpha, k)
    } else {
      // 弾ける: 80ms で 1.0→1.2 倍に膨らんで形がほどけ、6〜8 個の光の粒になって上へ散る (200ms で消える)。
      const B = BRIDE_OKAERI
      if (hs.popMs < B.heartPopMs) {
        const p = hs.popMs / B.heartPopMs
        drawHeart(ctx, x, y, size * (1 + 0.2 * p), hs.alphaAtPop * (1 - p), k, p)
      }
      const t = hs.popMs / B.heartBurstMs
      const n = 6 + Math.floor(rnd(k, 51) * 3)
      ctx.save()
      ctx.globalCompositeOperation = 'lighter'
      for (let i = 0; i < n; i++) {
        const ang = -Math.PI / 2 + (i / (n - 1) - 0.5) * 2.2 + (rnd(i + k * 9, 52) - 0.5) * 0.4
        const sp = 18 + 22 * rnd(i + k * 9, 53)
        const e = 1 - (1 - t) * (1 - t)
        const px = x + Math.cos(ang) * sp * e * 0.8
        const py = y + Math.sin(ang) * sp * e - 6 * e
        // 粒は♡がほどけるのと入れ替わりに灯る (弾けた瞬間に粒が1点へ重なって白く光らないように)。
        const fadeIn = Math.min(1, hs.popMs / B.heartPopMs)
        const a = hs.alphaAtPop * fadeIn * Math.pow(1 - t, 1.2) * (0.75 + 0.25 * Math.sin(hs.popMs * 0.08 + i)) * 0.8
        softDot(ctx, px, py, 1.1 + 0.9 * rnd(i + k * 9, 54), i % 2 === 0 ? SILVER : SAKURA, a)
      }
      ctx.restore()
    }
  }
  ctx.restore()
}

/** 床の上・キャラの後ろ: 騎士の残り火。 */
export function drawExecUniqueFxBehind(ctx: CanvasRenderingContext2D, state: GameState, footY: number, height: number): void {
  // dev 計測フックはこのコマの最初に描く層 (後ろ) で空にする (手前の層が続きを書く)。
  if (import.meta.env.DEV) (globalThis as { __execFxProbe?: FxProbe }).__execFxProbe = { blade: null, hearts: [] }
  const k = knightActiveMs(state)
  if (k) drawKnightEmbers(ctx, state, footY, height, k.ms)
  // 突進の光の尾の身 (キャラの後ろ)。
  const w = knightChargeWake(state, footY, height)
  if (w && w.ph.bodyAlpha > 0.003) drawBandShape(ctx, w.g, w.ms, w.ph, 'body', { coreFromTip: true, coreMul: 0.8, wake: true })
  // 光の尾から落ちて通った所に残る光の粒 (キャラの後ろ・10-12 K-3)。
  if (w) drawKnightWakeSparks(ctx, state, footY, height, w)
}

// dev 計測フック: このコマに描いた光の帯の始まりと♡の位置 (撮影が体とのずれを測るため・scripts/capture_exec_unique_polish.mjs)。
type FxProbe = { blade: { tipX: number; tipY: number; frontX: number } | null; hearts: { k: number; kind: string; x: number; y: number; size: number }[]; wakeSparks?: number }
function fxProbe(): FxProbe {
  const g = globalThis as { __execFxProbe?: FxProbe }
  return g.__execFxProbe ?? (g.__execFxProbe = { blade: null, hearts: [] })
}

/** キャラの手前: 騎士の光の帯・花嫁の♡。 */
export function drawExecUniqueFxFront(ctx: CanvasRenderingContext2D, state: GameState, footY: number, height: number): void {
  const plan = state.boss.currentAttack?.uniqueExec
  if (plan?.boss === 'vigil-knight' && plan.form === 'charge') {
    // 突進の光の尾の前端のにじみ・火の粉・砕けた粉 (キャラの手前)。
    const w = knightChargeWake(state, footY, height)
    if (w && (w.ph.bodyAlpha > 0.003 || w.g.parryCut)) {
      if (import.meta.env.DEV) fxProbe().blade = { tipX: w.g.tipX, tipY: w.g.tipY, frontX: w.g.frontX }
      drawBandShape(ctx, w.g, w.ms, w.ph, 'sparkle')
    }
  } else {
    const k = knightActiveMs(state)
    if (k) drawKnightBlade(ctx, state, footY, height, k.ms)
  }
  if (plan?.boss === 'bride') drawBrideHearts(ctx, state, plan, footY, height)
}
